<template>
    <div class="conversation-container">
        <table-layout
            :page-counter="pageCounter"
            :pageSize="numberElementsPerPage"
            :total="total"
            :current-page.sync="page"
            :currentChange="getDataSetPage"
            :queryString.sync="queryString"
            v-loading="loading"
            :query-placeholder="$t('common.search')"
            :search="searchData"
            :searchable="false"
            :sortables="false"
            :dataSource="conversationList"
            :isShowPaginationText="false"
            :selection="false"
        >
            <template slot="cols">
                <el-table-column :label="$t('classes.classes_details.conversation.table.learner')" align="center" prop="user_id">
                    <template slot-scope="scope">
                        <div class="user-info">
                            <div class="owner-info">
                                <router-link :to="`/organization/${getCurrentClass.organization_id}/students/${scope.row.user_id}`">
                                <p>{{ `${scope.row.first_name} ${scope.row.last_name}` }}</p>
                                <p>{{ scope.row.email }}</p>
                                </router-link>
                            </div>
                        </div>
                    </template>
                </el-table-column>
                <el-table-column :label="$t('classes.classes_details.conversation.table.date')" prop="started_at" align="center">
                    <template slot-scope="scope">
                        {{ formatDate(scope.row.started_at) }}
                    </template>
                </el-table-column>
                <el-table-column :label="$t('classes.classes_details.conversation.table.duration')" align="center" prop="duration_in_secs">
                    <template slot-scope="scope">
                        {{ intervalToHours(scope.row.duration_in_secs) }}
                    </template>
                </el-table-column>
                <el-table-column :label="$t('classes.classes_details.conversation.table.status')" align="center" prop="status">
                    <template slot-scope="scope">
                        {{ formatStatus(scope.row.status) }}
                    </template>
                </el-table-column>
                <el-table-column :label="$t('classes.classes_details.conversation.table.senario')" align="center" prop="scenario_id">
                    <template slot-scope="scope">
                        {{ scope.row.scenario_id }}
                    </template>
                </el-table-column>
                <el-table-column :label="$t('classes.classes_details.conversation.table.download_transcript')" align="center">
                    <template slot-scope="scope">
                        <el-button type="primary" size="mini" @click="downloadTranscript(scope.row.conversation_id)">
                            <i class="el-icon-download"></i>
                        </el-button>
                    </template>
                </el-table-column>
                <el-table-column :label="$t('classes.classes_details.conversation.table.report_issue')" align="center">
                    <template slot-scope="scope">
                        <el-button type="primary" size="mini" @click="reportIssue(scope.row.conversation_id)">
                            <i class="el-icon-s-flag"></i>
                        </el-button>
                    </template>
                </el-table-column>
            </template>
        </table-layout>
    </div>
</template>

<script>
import axios from '@/utils/axios'
import { mapGetters, mapActions } from 'vuex';
import TableLayout from "@/components/common/v2/ElsaBaseTableLayout";
import { always, is, compose, invoker, toString } from 'ramda';
import { PAGE_COUNTER, NUMBER_ELEMENTS_PER_PAGE, DATE_FORMAT_WITH_TIME } from '@/constants/generic';
import { format } from 'date-fns';

const padStart = compose(invoker(2, "padStart")(2, "0"), toString);
export default {
    components: { TableLayout },
    props: ['groupId'],
    data() {
        return {
            queryString: '',
            conversationList: [],
            loading: false,
            ordering: "",
            page: 1,
            total: 0,
        }
    },
    computed: {
        ...mapGetters({
            getCurrentClass: 'classes/getCurrentClass',
        }),
        pageCounter: always(PAGE_COUNTER),
        numberElementsPerPage: always(NUMBER_ELEMENTS_PER_PAGE)
    },
    methods:{
        async getConversationList(){
            this.loading = true;
            try {
                const url_conversations = `v1/groups/${this.groupId}/ai-conversations`;
                const { data } = await axios.get(url_conversations, {
                    params: {
                        ordering: this.ordering,
                        page: this.page,
                        search: this.queryString,
                    }
                });
                this.conversationList = data.results;
                this.total = data.count;
                this.loading = false;
            } catch (e) {
                throw e;
            } finally {
                this.loading = false;
            }
        },
        searchData(queryString) {
            this.queryString = queryString;
            this.getConversationList();
        },
        getDataSetPage(page) {
            this.page = page;
            this.getConversationList();
        },
        intervalToHours(cellValue) {
            if (!is(Number, cellValue)) {
                return cellValue;
            }
            const minutes = Math.floor(cellValue / 60);
            const seconds = cellValue - minutes * 60;

            return `${padStart(minutes)}:${padStart(seconds)}`;
        },
        async downloadTranscript(conversation_id) {
            if (conversation_id == null) {
                return;
            }
            try {
                this.loading = true;
                const url = `v1/groups/${this.groupId}/role-play-conversation/${conversation_id}/download`;
                const response = await axios({
                    method: 'GET',
                    url: url,
                });
                const blod = new Blob([response.data], { type: 'text/csv' });
                const fileName = `${conversation_id}.csv`;

                // Create a link element and trigger the download
                const link = document.createElement('a');
                link.href = window.URL.createObjectURL(blod);
                link.download = fileName;

                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            } catch (error) {
                throw error;
            } finally {
                this.loading = false;
            }
        },
        reportIssue(conversation_id) {
            const conversation = this.conversationList.find((x) => x.conversation_id === conversation_id);
            this.setAIConversationReport(conversation)
            return this.$router.push({
                name: 'report-role-play',
                params: {
                    organizationId: this.getCurrentClass.organization_id,
                    classId: this.groupId,
                }
            })
        },
        formatDate(date) {
            return isNaN(Date.parse(date))
            ? date
            : format(new Date(date), DATE_FORMAT_WITH_TIME);
        },
        formatStatus(status) {
            return status === 'complete' ? 'Finished' : 'Incomplete';
        },
        ...mapActions({
            setAIConversationReport: 'classes/setAIConversationReport',
        }),
    },
    async mounted(){
        try {
            await this.getConversationList();
        } catch (e) {
            throw e;
        }
    },
}
</script>
<style lang="scss" scoped>
    @import '../../../scss/_colors.scss'; 
    .conversation-container {
        margin-top: -30px;
    }
    .user-info {
    display: flex;
    align-items: center;
    font-weight: 500;
    font-size: 14px;
    color: $darker-blue;
    .owner-info {
      p {
        font-weight: 500;
        font-size: 16px;
        text-transform: capitalize;
        color: $hyperlink;
        margin: 0;
        text-align: initial;
        display: flex;
        align-items: center;
        &:last-of-type {
          font-size: 14px;
          color: $light-blue-2;
          text-transform: none;
          line-height: 34px;
        }
      }
    }
  }
</style>
